import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Search, X, Check } from "lucide-react";

/**
 * Buscador/selector de Artículo Maestro. Se muestra como input con
 * autocompletado. Al elegir, llama onChange({ id, sku, name }).
 */
export default function MasterPartPicker({ value, onChange, testid = "master-picker" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!open) return;
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/master-parts", { params: { q } });
        setList(r.data || []);
      } catch {
        setList([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, open]);

  useEffect(() => {
    function onClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const clear = () => {
    onChange(null);
    setQ("");
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testid}>
      {value?.id ? (
        <div className="flex items-center gap-2 ds-card px-3 py-2 text-sm">
          <Check size={14} className="text-[#6E8B3D]" />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{value.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
              {value.sku}
            </div>
          </div>
          <button
            type="button"
            onClick={clear}
            className="p-1 hover:bg-[#F7F7F4] rounded"
            data-testid={`${testid}-clear`}
            title="Quitar vinculación"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
          <input
            type="text"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Buscar por SKU o nombre..."
            data-testid={`${testid}-input`}
            className="w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none pl-9 pr-3 py-2 text-sm"
          />
        </div>
      )}
      {!value?.id && open && (
        <div className="absolute left-0 right-0 top-full mt-1 max-h-64 overflow-auto ds-card z-30 shadow-hard">
          {loading && <div className="px-3 py-2 text-xs text-[#525252]">Buscando…</div>}
          {!loading && list.length === 0 && (
            <div className="px-3 py-2 text-xs text-[#525252]">No hay maestros. Crea uno primero.</div>
          )}
          {list.map((m) => (
            <button
              type="button"
              key={m.id}
              onClick={() => { onChange({ id: m.id, sku: m.sku, name: m.name, warranty_months: m.warranty_months ?? 0 }); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-[#F0F4E8] flex items-center gap-2"
              data-testid={`${testid}-option-${m.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{m.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
                  {m.sku} · Stock {m.stock}
                </div>
              </div>
              {!m.active && (
                <span className="text-[10px] font-mono uppercase text-[#525252]">inactivo</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
